import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ConfigProvider from '../config-provider';
import Input from '../input';
import Select from '../select';
import Button from '../button';
import Icon from '../icon';
import { obj, func, KEYCODE } from '../util';
import zhCN from '../locale/zh-cn';

const { Group } = Input;
const { AutoComplete } = Select;

/**
 * Search
 * @description 输入框部分继承 Select.AutoComplete 的能力，可以直接用AutoComplete 的 api
 */
class Search extends React.Component {
  static displayName = 'Search';

  static propTypes = {
    /**
     * 样式前缀
     */
    prefix: PropTypes.string,
    /**
     * 样式前缀
     */
    autoFocus: PropTypes.bool,
    /**
     * 形状
     */
    shape: PropTypes.oneOf(['normal', 'simple']),
    /**
     * 类型 shape=normal: primary/secondary; shape=simple: normal/dark/nav-search;
     */
    type: PropTypes.oneOf(['primary', 'secondary', 'normal', 'dark', 'nav-search']),
    /**
     * 后缀，仅在type为normal时有效
     */
    addonAfter: PropTypes.node,
    /**
     * 搜索框默认值
     */
    defaultValue: PropTypes.string,
    /**
     * 搜索框数值
     */
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /**
     * 输入关键字时的回掉
     * @param {Object} value 输入值
     */
    onChange: PropTypes.func,
    /**
     * 点击搜索按钮触发的回调
     * @param {Object} value 输入值
     */
    onSearch: PropTypes.func,
    /**
     * 选择器默认值
     */
    defaultFilterValue: PropTypes.string,
    /**
     * 选择器
     */
    filter: PropTypes.array,
    /**
     * 选择器值
     */
    filterValue: PropTypes.string,
    /**
     * 选择器发生变化时回调
     * @param {Object} filter value
     */
    onFilterChange: PropTypes.func,
    /**
     * 搜索框下拉联想列表
     */
    dataSource: PropTypes.array,
    /**
     * 默认提示
     */
    placeholder: PropTypes.string,
    /**
     * button 的内容
     */
    searchText: PropTypes.node,
    /**
     * 自定义样式
     */
    style: PropTypes.object,
    /**
     * 样式名称
     */
    className: PropTypes.string,
    /**
     * 选择器的props
     */
    filterProps: PropTypes.object,
    /**
     * 按钮的额外属性
     */
    buttonProps: PropTypes.object,
    /**
     * 自定义渲染的的下拉框
     */
    popupContent: PropTypes.node,
    /**
     * 是否跟随滚动
     */
    followTrigger: PropTypes.bool,
    /**
     * 自定义渲染的的下拉框
     */
    visible: PropTypes.bool,
    /**
     * 是否显示清除按钮
     */
    hasClear: PropTypes.bool,
    /**
     * 是否显示搜索按钮
     */
    hasIcon: PropTypes.bool,
    /**
     * 是否禁用
     */
    disabled: PropTypes.bool,
    locale: PropTypes.object,
    rtl: PropTypes.bool,

    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
  };

  static defaultProps = {
    prefix: 'next-',
    shape: 'simple',
    type: 'normal',
    hasIcon: true,
    filter: [],
    locale: zhCN.Search,
    buttonProps: {},
    onChange: func.noop,
    onSearch: func.noop,
    onFilterChange: func.noop,
    autoFocus: false,
    hasClear: true,
    disabled: false,
  };

  inputRef = React.createRef();

  autoCompleteRef = React.createRef();

  constructor(props) {
    super(props);

    const value = 'value' in props ? props.value : props.defaultValue;
    const filterValue = 'filterValue' in props ? props.filterValue : props.defaultFilterValue;

    this.state = {
      value: typeof value === 'undefined' ? '' : value,
      filterValue,
      searchFocus: props.autoFocus && !props.disabled ? true : -1, //  -1 or boolean
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const state = {};
    if ('value' in nextProps) {
      state.value = typeof nextProps.value === 'undefined' ? '' : nextProps.value;
    }
    if ('filterValue' in nextProps) {
      state.filterValue = typeof nextProps.filterValue === 'undefined' ? '' : nextProps.filterValue;
    }
    this.setState(state);
  }

  onChange = (value, ...argv) => {
    if (!('value' in this.props)) {
      this.setState({ value });
    }

    this.props.onChange(value, ...argv);
  };

  onSearch = () => {
    if (this.props.disabled) {
      return;
    }
    this.props.onSearch(this.state.value, this.state.filterValue);
  };

  onFilterChange = filterValue => {
    if (!('filterValue' in this.props)) {
      this.setState({ filterValue });
    }

    this.props.onFilterChange(filterValue);
  };

  onKeyDown = e => {
    if (this.props.disabled) {
      return;
    }
    if (e.keyCode !== KEYCODE.ENTER) {
      return;
    }
    this.onSearch();
  };

  focus = () => {
    if (this.props.disabled) {
      return;
    }
    this.inputRef.current && this.inputRef.current.focus();
    if (this.autoCompleteRef.current && this.autoCompleteRef.current.getInstance) {
      const ins = this.autoCompleteRef.current.getInstance();
      ins && ins.focusInput && ins.focusInput();
    }
    this.setState({
      searchFocus: true,
    });
  };

  onClickMask = () => {
    this.focus();
  };

  onInputFocus = () => {
    this.setState({
      searchFocus: true,
    });
    if (typeof this.props.onFocus === 'function') {
      this.props.onFocus();
    }
  };

  onInputBlur = () => {
    this.setState({
      searchFocus: false,
    });
    if (typeof this.props.onBlur === 'function') {
      this.props.onBlur();
    }
  };

  renderInputMask() {
    const { prefix, disabled, placeholder } = this.props;

    return (
      <div className={`${prefix}search-mask`} onClick={this.onClickMask}>
        <Icon type="search" aria-disabled={disabled} />
        <span className={`${prefix}search-mask-text`}>{placeholder}</span>
      </div>
    );
  }

  renderInput() {
    const { hasClear, disabled, placeholder, autoFocus } = this.props;

    return (
      <Input
        ref={this.inputRef}
        autoFocus={autoFocus}
        hasClear={hasClear}
        disabled={disabled}
        placeholder={this.state.searchFocus === true ? placeholder : undefined}
        value={this.state.value}
        onChange={this.onChange}
        onFocus={this.onInputFocus}
        onBlur={this.onInputBlur}
      />
    );
  }

  renderAddonAfter = () => {
    const { prefix, type, addonAfter } = this.props;
    if (type !== 'normal' || !addonAfter) {
      return null;
    }
    return <span className={`${prefix}search-addon-after`}>{addonAfter}</span>;
  };

  renderSearchButton() {
    const { buttonProps, disabled, locale, hasIcon, prefix, searchText } = this.props;

    const buttonCls = classNames({
      [`${prefix}search-btn`]: true,
      [buttonProps.className]: !!buttonProps.className,
    });

    return (
      <Button
        tabIndex="0"
        aria-disabled={disabled}
        aria-label={locale.buttonText}
        className={buttonCls}
        disabled={disabled}
        {...buttonProps}
        onClick={this.onSearch}
        onKeyDown={this.onKeyDown}
      >
        {hasIcon ? <Icon type="search" /> : null}
        {searchText ? <span className={`${prefix}search-btn-text`}>{searchText}</span> : null}
      </Button>
    );
  }

  renderSearchIcon() {
    const { buttonProps, disabled, locale, hasIcon, prefix } = this.props;

    const iconCls = classNames({
      [`${prefix}search-icon`]: true,
      [buttonProps.className]: !!buttonProps.className,
    });

    return (
      hasIcon && (
        <Icon
          type="search"
          tabIndex="0"
          role="button"
          aria-disabled={disabled}
          aria-label={locale.buttonText}
          {...buttonProps}
          className={iconCls}
          onClick={this.onSearch}
          onKeyDown={this.onKeyDown}
        />
      )
    );
  }

  render() {
    const {
      shape,
      autoFocus,
      filter,
      hasIcon,
      disabled,
      placeholder,
      type,
      className,
      style,
      prefix,
      searchText,
      dataSource,
      filterProps,
      buttonProps,
      popupContent,
      followTrigger,
      hasClear,
      visible,
      locale,
      rtl,
      ...others
    } = this.props;

    const cls = classNames({
      [`${prefix}search`]: true,
      [`${prefix}search-disabled`]: disabled,
      [`${prefix}search-${shape}`]: true,
      [`${prefix}${type}`]: type,
      [`${prefix}medium`]: true,
      [`${prefix}search-focus`]: this.state.searchFocus === true,
      [className]: !!className,
    });

    let searchIcon = null;
    let searchBtn = null;
    let filterSelect = null;

    if (shape === 'simple') {
      searchIcon = this.renderSearchIcon();
    } else {
      searchBtn = this.renderSearchButton();
    }

    if (filter.length > 0) {
      filterSelect = (
        <Select
          {...filterProps}
          followTrigger={followTrigger}
          hasBorder={false}
          dataSource={filter}
          disabled={disabled}
          value={this.state.filterValue}
          onChange={this.onFilterChange}
        />
      );
    }

    const othersAttributes = obj.pickOthers(Search.propTypes, others);
    if (visible !== undefined) {
      // 受控属性 visible 不能直接写在组件上
      othersAttributes.visible = Boolean(visible);
    }
    const dataAttr = obj.pickAttrsWith(others, 'data-');

    const left = (
      <Group
        addonBefore={filterSelect}
        className={`${prefix}search-left`}
        addonBeforeClassName={`${prefix}search-left-addon`}
        addonAfter={this.renderAddonAfter()}
      >
        <AutoComplete
          aria-label={locale.buttonText}
          showEmpty={false}
          {...othersAttributes}
          autoFocus={autoFocus}
          followTrigger={followTrigger}
          role="searchbox"
          hasClear={hasClear}
          className={`${prefix}search-input`}
          placeholder={placeholder}
          dataSource={dataSource}
          innerBefore={searchIcon}
          onPressEnter={this.onSearch}
          value={this.state.value}
          onChange={this.onChange}
          onFocus={this.onInputFocus}
          onBlur={this.onInputBlur}
          popupContent={popupContent}
          disabled={disabled}
          ref={this.autoCompleteRef}
        />
      </Group>
    );

    if (type === 'nav-search') {
      const wrapCls = classNames({
        [`${prefix}search`]: true,
        [`${prefix}${type}`]: type,
        [`${prefix}search-disabled`]: disabled,
        [`${prefix}search-init`]: this.state.searchFocus === -1,
        [`${prefix}search-focus`]: this.state.searchFocus === true,
        [`${prefix}search-has-query`]: !!this.state.value,
        [className]: !!className,
      });

      return (
        <span className={wrapCls} style={style} dir={rtl ? 'rtl' : undefined} {...dataAttr}>
          {this.renderInputMask()}
          {this.renderInput()}
        </span>
      );
    }

    return (
      <span className={cls} style={style} {...dataAttr} dir={rtl ? 'rtl' : undefined}>
        {searchBtn ? <Group addonAfter={searchBtn}>{left}</Group> : left}
      </span>
    );
  }
}

export default ConfigProvider.config(Search, {
  transfrom: /* istanbul ignore next */ (props, deprecated) => {
    const { onInputFocus, overlayVisible, combox, ...others } = this.props;

    const newprops = others;

    if (onInputFocus) {
      deprecated('onInputFocus', 'onFocus', 'Search');
      newprops.onFocus = onInputFocus;
    }
    if ('overlayVisible' in this.props) {
      deprecated('overlayVisible', 'visible', 'Search');
      newprops.visible = overlayVisible;
    }
    if (combox) {
      deprecated('combox', 'popupContent', 'Search');
      newprops.popupContent = combox;
    }

    return newprops;
  },
});
